import Pagination from 'components/Pagination';
import { NumberOfOrdersPerPageSettingWrapper } from 'components/SettingsWrappers';
import SortButtons, { SortButtonsValue } from 'components/SortButtons/SortButtons';
import Spinner from 'components/Spinner';
import OrderTooltip from 'containers/Orders/OrderTooltip';
import { deleteOrder, getOrderPdf, getOrderStatus, changeOrderAuthorization } from 'lib/communication/orders';
import { OptionType, Order, OrderDirection, OrderStatusName } from 'lib/types';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Collapse, Modal, OverlayTrigger, Popover, Row, Table, Tooltip } from 'react-bootstrap';
import { FaCheck, FaCopy, FaFileAlt, FaPlus, FaRegClock, FaRegEdit, FaRegFilePdf, FaRegTrashAlt, FaTimes, FaPenAlt, FaBan } from 'react-icons/fa';
import { MdMoreHoriz } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { OrdersState } from '../../containers/Orders/Orders';
import { AppState, dataActions } from 'reducers';
import ButtonComponent from 'components/ButtonComponent';
import './TableView.scss';
import Dropdown from 'components/Dropdown/Dropdown';
import permissionService from 'services';
import { useDevice } from 'lib/hooks/useDevice';
import { GA_orderActionUsed } from 'lib/gtm-utils';

interface TableViewProps {
    openedMobile: boolean[];
    setOpenedMobile: React.Dispatch<React.SetStateAction<boolean[]>>;
    handleSortChange: (value: SortButtonsValue) => void;
    handleRefreshOrderList: () => void;
    ordersState: OrdersState;
    error: boolean;
    orderNumber: string;
    customerOrderNumber: string;
    description: string;
    from: string;
    to: string;
    orders: {
        accepted: Order[] | null;
        unauthorized: Order[] | null;
        waiting: Order[] | null;
        processing: Order[] | null;
        completed: Order[] | null;
        all: Order[] | null;
    };
}

const ORDER_STATUS_ICONS_MOBILE: Record<string, JSX.Element> = {
    Brak: <FaBan />,
    Przyjęte: <FaCheck />,
    Oczekujące: <FaRegClock />,
    Realizowane: <FaRegClock />,
    Zrealizowane: <FaCheck />,
    Zamknięte: <FaTimes />
};

const sortOptions: OptionType<string>[] = [
    { label: 'Po numerze malejąco', value: 'orderNumber-desc' },
    { label: 'Po numerze rosnąco', value: 'orderNumber-asc' },
    { label: 'Po numerze klienta malejąco', value: 'customerOrderNumber-desc' },
    { label: 'Po numerze klienta rosnąco', value: 'customerOrderNumber-asc' },
    { label: 'Po dacie malejąco', value: 'date-desc' },
    { label: 'Po dacie rosnąco', value: 'date-asc' },
    { label: 'Po statusie malejąco', value: 'status-desc' },
    { label: 'Po statusie rosnąco', value: 'status-asc' },
    { label: 'Po wartości netto malejąco', value: 'net-desc' },
    { label: 'Po wartości netto rosnąco', value: 'net-asc' },
    { label: 'Po wartości brutto malejąco', value: 'gross-desc' },
    { label: 'Po wartości brutto rosnąco', value: 'gross-asc' },
    { label: 'Po wadze malejąco', value: 'weight-desc' },
    { label: 'Po wadze rosnąco', value: 'weight-asc' }
];

const TableView = (props: TableViewProps) => {
    const { isDesktop } = useDevice();
    const history = useHistory();
    const dispatch = useDispatch();

    const [maxNet, setMaxNet] = useState(0);
    const [maxGross, setMaxGross] = useState(0);
    const [maxWeight, setMaxWeight] = useState(0);

    const [orderBy, setOrderBy] = useState('date');
    const [orderDirection, setOrderDirection] = useState(OrderDirection.Desc);

    const [errorPdf, setErrorPdf] = useState(false);

    const [orderToAuthorize, setOrderToAuthorize] = useState<Order | undefined>(undefined);
    const [orderToDelete, setOrderToDelete] = useState<Order | undefined>(undefined);
    const [orderToCopy, setOrderToCopy] = useState<Order | undefined>(undefined);
    const [orderToAppend, setOrderToAppend] = useState<Order | undefined>(undefined);
    const [showAuthorizationConfirmation, setShowAuthorizationConfirmation] = useState(false);
    const [showAuthorizationStatus, setShowAuthorizationStatus] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showDeleteStatus, setShowDeleteStatus] = useState(false);
    const [showCopyOrderConfirmation, setShowCopyOrderConfirmation] = useState(false);
    const [showAppendOrderConfirmation, setShowAppendOrderConfirmation] = useState(false);

    const isFetchingOrders = useSelector((state: AppState) => state.data.orders.props.isFetchingOrders);
    const pdfIsFetching = useSelector((state: AppState) => state.data.orders.props.pdfIsFetching);
    const prevOrderStatus = useSelector((state: AppState) => state.data.orders.orderStatusInfo.orderStatus);

    const [selectedFilteredOrders, setSelectedFilteredOrders] = useState([] as Order[]);

    const [pageNumber, setPageNumber] = useState(0);

    useEffect(() => {
        setPageNumber(0);
    }, [selectedFilteredOrders]);

    const calculateNumberOfPagesForPageSize = (pageSize: number): number => Math.max(1, _.ceil(_.size(selectedFilteredOrders) / pageSize));
    const numberOfPages = calculateNumberOfPagesForPageSize(props.ordersState.pageSize);
    const ordersOnPage = _.take(_.slice(selectedFilteredOrders, pageNumber * props.ordersState.pageSize), props.ordersState.pageSize);

    const getAllSelectedOrders = () => Object.keys(props.orders)
        .reduce((acc, key) => (props.ordersState.statusFilter[key as OrderStatusName] && props.orders[key as OrderStatusName] ?
            acc.concat(props.orders[key as OrderStatusName] as Order[]) : acc), [] as Order[]);

    const filterAndSetOrders = () => {
        const orderByIteratees = [props.ordersState.orderBy];
        const orderDirections = [props.ordersState.orderDirection];
        if (!orderByIteratees.includes('orderNumber')) {
            orderByIteratees.push('orderNumber');
            orderDirections.push(OrderDirection.Desc);
        }
        setSelectedFilteredOrders(_.orderBy(getAllSelectedOrders(), orderByIteratees, orderDirections));
    };

    const handleEditOrder = (order: Order) => {
        GA_orderActionUsed('edit', order, isDesktop);
        history.push({
            pathname: `${process.env.PUBLIC_URL}/new-order`,
            state: {
                ...(typeof history.location.state === 'object' ? history.location.state : {}),
                editedOrderId: order.id
            }
        });
    };

    const handleCopyOrder = (order: Order) => {
        GA_orderActionUsed('copy', order, isDesktop);
        history.push({
            pathname: `${process.env.PUBLIC_URL}/new-order`,
            state: {
                ...(typeof history.location.state === 'object' ? history.location.state : {}),
                copiedOrderId: order.id
            }
        });
    };

    const handleDownloadOrderPdf = (order: Order) => {
        GA_orderActionUsed('pdf', order, isDesktop);
        getOrderPdf(order.id, order.net, order.vat, order.gross);
    };

    const handleOrderDelete = () => {
        setShowDeleteConfirmation(false);
        if (orderToDelete) {
            deleteOrder(orderToDelete.id, orderToDelete.gross).then((result) => {
                if (result) {
                    setShowDeleteStatus(true);
                }
            });
            props.setOpenedMobile([false]);
        }
    };

    const handleUpdateOrderAuthorization = () => {
        setShowAuthorizationConfirmation(false);
        if (orderToAuthorize) {
            changeOrderAuthorization(orderToAuthorize.id, orderToAuthorize.authorizationStatus, orderToAuthorize.authorizationStatus === 'Autoryzowane' ? 'Brak' : 'Autoryzowane').then((result) => {
                if (result) {
                    setShowAuthorizationStatus(true);
                    props.handleRefreshOrderList();
                }
            });
        }
    };

    const handleAppendOrder = (order: Order) => {
        GA_orderActionUsed('append', order, isDesktop);
        history.push({
            pathname: `${process.env.PUBLIC_URL}/new-order`,
            state: {
                ...(typeof history.location.state === 'object' ? history.location.state : {}),
                appendedOrderId: order.id
            }
        });
    };

    // calculate width of rendered prices in table for design purpose
    const netPriceWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxNet.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.App-body')?.appendChild(d);
        return d.getBoundingClientRect().width;
    }, [maxNet]);

    const grossPriceWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxGross.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.App-body')?.appendChild(d);
        return d.getBoundingClientRect().width;
    }, [maxGross]);

    const weightWidth = useMemo(() => {
        const d = document.createElement('div');
        d.innerHTML = maxWeight.toFixed(2);
        d.classList.add('measurer');
        document.querySelector('.App-body')?.appendChild(d);
        return d.getBoundingClientRect().width;
    }, [maxWeight]);

    const handlePageSizeUpdate = (newValue: number) => {
        const newNumberOfPages = calculateNumberOfPagesForPageSize(newValue);
        const pushedPageNumber = pageNumber + 1 > newNumberOfPages ? newNumberOfPages : pageNumber + 1;
        history.push(`${process.env.PUBLIC_URL}/orders/${pushedPageNumber}`, {
            ...props.ordersState,
            pageSize: newValue,
            orderNumber: props.orderNumber,
            customerOrderNumber: props.customerOrderNumber,
            description: props.description
        });
    };

    const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.currentTarget.value);
        handlePageSizeUpdate(newPageSize);
    };

    const showOrderStatus = (order: Order) => {
        dispatch(dataActions.setOrderStatus({
            ...prevOrderStatus,
            header: {
                ...prevOrderStatus.header,
                orderNumber: order.orderNumber
            }
        })); // Required so that order id is available before receiving response from the server
        getOrderStatus(order.id);
        dispatch(dataActions.setShowOrderStatus(true));
    };

    // after getOrdersList finishes and sets new orders
    useEffect(() => {
        filterAndSetOrders();
    }, [props.orders, props.ordersState.orderBy, props.ordersState.orderDirection]);

    useEffect(() => {
        if (ordersOnPage.length !== 0) {
            setMaxNet((_.maxBy(ordersOnPage, (order) => order.net) as Order).net);
            setMaxGross((_.maxBy(ordersOnPage, (order) => order.gross) as Order).gross);
            setMaxWeight((_.maxBy(ordersOnPage, (order) => order.weight) as Order).weight);
        }
    }, [ordersOnPage]);

    useEffect(() => {
        if (_.size(selectedFilteredOrders) !== 0 && ordersOnPage.length <= 0) {
            history.push(`${process.env.PUBLIC_URL}/orders`, {
                ...props.ordersState,
                orderNumber: props.orderNumber,
                customerOrderNumber: props.customerOrderNumber,
                description: props.description
            });
        }
    }, [ordersOnPage]);

    useEffect(() => {
        setOrderBy(props.ordersState.orderBy);
        setOrderDirection(props.ordersState.orderDirection);
    }, [props.ordersState]);

    return (
        <>
            <Modal show={errorPdf || pdfIsFetching} onHide={() => setErrorPdf(false)} centered className='pdf-modal'>
                <Modal.Header closeButton={errorPdf} className='pdf-modal-header' />
                <Modal.Body className='pdf-modal-body'>
                    <Spinner showError={errorPdf} toPdf />
                </Modal.Body>
                <Modal.Footer className='pdf-modal-footer'>
                    {!pdfIsFetching && (
                        <ButtonComponent
                            text='Zamknij'
                            onClick={() => setErrorPdf(false)}
                        />
                    )}
                </Modal.Footer>
            </Modal>
            {isDesktop && (
                <Row className='justify-content-end'>
                    <NumberOfOrdersPerPageSettingWrapper
                        headerText='Ilość na stronie'
                        id='rowCount'
                        initialValue={props.ordersState.pageSize}
                        changeHandler={handleChangePageSize}
                        inTable
                        isDesktop={isDesktop}
                    />
                    <Col xs='auto' style={{ marginLeft: '15px', flexGrow: 1 }}>
                        <div className='d-flex justify-content-end'>
                            <Pagination
                                selected={pageNumber}
                                size={numberOfPages}
                                onSelect={(p) => setPageNumber(p)}
                            />
                        </div>
                    </Col>
                </Row>
            )}
            {isDesktop ? (
                <Table className='Order-table' size='sm' borderless striped>
                    <thead>
                        <tr>
                            <th className='cell-width-15per'>
                                Numer
                                <SortButtons
                                    value={{ direction: orderDirection, group: 'orderNumber' }}
                                    onChange={props.handleSortChange}
                                    orderBy={orderBy}
                                />
                            </th>
                            <th className='cell-width-15per'>
                                Numer klienta
                                <SortButtons
                                    value={{ direction: orderDirection, group: 'customerOrderNumber' }}
                                    onChange={props.handleSortChange}
                                    orderBy={orderBy}
                                />
                            </th>
                            <th className='cell-width-9per'>
                                Data
                                <SortButtons
                                    value={{ direction: orderDirection, group: 'date' }}
                                    onChange={props.handleSortChange}
                                    orderBy={orderBy}
                                />
                            </th>
                            <th className='cell-width-9per'>
                                Status
                                <SortButtons
                                    value={{ direction: orderDirection, group: 'status' }}
                                    onChange={props.handleSortChange}
                                    orderBy={orderBy}
                                />
                            </th>
                            <th className='cell-width-9per'>
                                Netto
                                <SortButtons
                                    value={{ direction: orderDirection, group: 'net' }}
                                    onChange={props.handleSortChange}
                                    orderBy={orderBy}
                                />
                            </th>
                            <th className='cell-width-9per'>
                                Brutto
                                <SortButtons
                                    value={{ direction: orderDirection, group: 'gross' }}
                                    onChange={props.handleSortChange}
                                    orderBy={orderBy}
                                />
                            </th>
                            <th className='cell-width-9per'>
                                Waga
                                <SortButtons
                                    value={{ direction: orderDirection, group: 'weight' }}
                                    onChange={props.handleSortChange}
                                    orderBy={orderBy}
                                />
                            </th>
                            <th className='cell-max-width-60'>Opis</th>
                            <th className='cell-max-width-60'>Akcje</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isFetchingOrders || props.error ?
                            <tr>
                                <td colSpan={10}><Spinner centered={false} showError={props.error} /></td>
                            </tr> :
                            <>
                                {ordersOnPage.map((order) => (
                                    <tr key={order.orderNumber}>
                                        <td className='cursor-pointer' onClick={() => { showOrderStatus(order); }}>{order.orderNumber}</td>
                                        <td className='cursor-pointer' onClick={() => { showOrderStatus(order); }}>{order.customerOrderNumber}</td>
                                        <td className='cursor-pointer' onClick={() => { showOrderStatus(order); }}>{order.date}</td>
                                        {order.status !== null ? (
                                            <OverlayTrigger
                                                overlay={<Tooltip id='order_status_description_tooltip'>{order.status}</Tooltip>}
                                            >
                                                <td className='cursor-pointer' onClick={() => { showOrderStatus(order); }}>{order.status}</td>
                                            </OverlayTrigger>
                                        ) : <td className='cursor-pointer' onClick={() => { showOrderStatus(order); }}>{order.status}</td>}
                                        <td><div className='price-td-div net-price-td cursor-pointer' onClick={() => { showOrderStatus(order); }} style={{ width: netPriceWidth }}>{order.net.toFixed(2)}</div></td>
                                        <td><div className='price-td-div gross-price-td cursor-pointer' onClick={() => { showOrderStatus(order); }} style={{ width: grossPriceWidth }}>{order.gross.toFixed(2)}</div></td>
                                        <td><div className='price-td-div wieght-price-td cursor-pointer' onClick={() => { showOrderStatus(order); }} style={{ width: weightWidth }}>{order.weight.toFixed(2)}</div></td>
                                        <td className='cell-width-50'>
                                            {((order.description && order.description.length !== 0) || (order.customerDescription && order.customerDescription.length !== 0)) && (
                                                <OverlayTrigger overlay={<OrderTooltip
                                                    id='order_description_tooltip'
                                                    messages={{
                                                        'Uwagi do zamówienia': order.description,
                                                        'Opis własny klienta': order.customerDescription
                                                    }}
                                                />}
                                                >
                                                    <FaFileAlt />
                                                </OverlayTrigger>
                                            )}
                                        </td>
                                        <td className='cell-width-140'>
                                            <OverlayTrigger
                                                trigger='click'
                                                placement='left'
                                                rootClose
                                                overlay={
                                                    <Popover className='popover-visibility' id={`order-${order.orderNumber}-action-popover`}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                            {permissionService.haveAccessToOperation('authorizeOrder') ?
                                                                <Button
                                                                    variant='link'
                                                                    className='action-button'
                                                                    style={{ color: 'black' }}
                                                                    onClick={() => {
                                                                        setOrderToAuthorize(order);
                                                                        setShowAuthorizationConfirmation(true);
                                                                    }}
                                                                >
                                                                    <FaPenAlt />
                                                                    {' '}
                                                                    Zmień autoryzację
                                                                </Button> : <></>}
                                                            {order.isEditable && permissionService.haveAccessToOperation('manageOrder') ?
                                                                <Button
                                                                    variant='link'
                                                                    className='action-button'
                                                                    style={{ color: 'black' }}
                                                                    onClick={() => handleEditOrder(order)}
                                                                >
                                                                    <FaRegEdit />
                                                                    {' '}
                                                                    Edytuj zamówienie
                                                                </Button> :
                                                                <></>}
                                                            {order.isDeletable && permissionService.haveAccessToOperation('manageOrder') ?
                                                                <Button
                                                                    variant='link'
                                                                    className='action-button'
                                                                    style={{ color: 'black' }}
                                                                    disabled={order.status === 'Zamknięte'}
                                                                    onClick={() => {
                                                                        setOrderToDelete(order);
                                                                        setShowDeleteConfirmation(true);
                                                                    }}
                                                                >
                                                                    <FaRegTrashAlt />
                                                                    {' '}
                                                                    Usuń zamówienie
                                                                </Button> :
                                                                <></>}
                                                            {order.isPrintable ?
                                                                <Button
                                                                    variant='link'
                                                                    className='action-button'
                                                                    style={{ color: 'black' }}
                                                                    onClick={() => {
                                                                        handleDownloadOrderPdf(order);
                                                                    }}
                                                                >
                                                                    <FaRegFilePdf />
                                                                    {' '}
                                                                    Pobierz PDF
                                                                </Button> :
                                                                <></>}
                                                            {order.isEditable && permissionService.haveAccessToOperation('manageOrder') ?
                                                                <Button
                                                                    variant='link'
                                                                    className='action-button'
                                                                    style={{ color: 'black', visibility: order.isEditable ? 'visible' : 'hidden' }}
                                                                    onClick={() => {
                                                                        setOrderToCopy(order);
                                                                        setShowCopyOrderConfirmation(true);
                                                                    }}
                                                                >
                                                                    <FaCopy />
                                                                    {' '}
                                                                    Kopiuj zamówienie
                                                                </Button> :
                                                                <></>}
                                                            {order.isEditable && permissionService.haveAccessToOperation('manageOrder') ?
                                                                <Button
                                                                    variant='link'
                                                                    className='action-button'
                                                                    style={{ color: 'black', visibility: order.isEditable ? 'visible' : 'hidden' }}
                                                                    onClick={() => {
                                                                        setOrderToAppend(order);
                                                                        setShowAppendOrderConfirmation(true);
                                                                    }}
                                                                >
                                                                    <FaPlus />
                                                                    {' '}
                                                                    Wystaw domówienie
                                                                </Button> : <></>}
                                                        </div>
                                                    </Popover>
                                                }
                                            >
                                                <Button
                                                    variant='link'
                                                    className='action-button'
                                                    style={{ color: 'black' }}
                                                >
                                                    <MdMoreHoriz />
                                                </Button>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                ))}
                            </>}
                    </tbody>
                </Table>
            ) : (
                <div>
                    <div className='sort-block-mobile'>
                        <span style={{ paddingRight: '10px' }}>Sortuj:</span>
                        <div style={{ width: '60%' }}>
                            <Dropdown<string>
                                isSearchable={false}
                                onChange={(event) => {
                                    const by = (event as OptionType<string>)?.value.split('-')[0];
                                    const direction = (event as OptionType<string>)?.value.split('-')[1] === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                                    setOrderBy(by);
                                    setOrderDirection(direction);
                                    props.handleSortChange({ direction, group: by });
                                }}
                                options={sortOptions}
                                value={sortOptions.find((option) => option.value === `${orderBy}-${orderDirection}`)}
                            />
                        </div>
                    </div>
                    {isFetchingOrders || props.error ?
                        <Spinner centered={false} showError={props.error} /> :
                        <>
                            {_.take(selectedFilteredOrders, (pageNumber + 1) * props.ordersState.pageSize).map((order, index) => (
                                <div
                                    key={index}
                                    className={`Order-block-item${props.openedMobile[index] ? ' rotate' : ''}`}
                                    onClick={() => {
                                        const arr = props.openedMobile.splice(0);
                                        arr[index] = !arr[index];
                                        props.setOpenedMobile(arr);
                                    }}
                                >
                                    <div>
                                        <div className='Order-block-item-content-left'>
                                            <span>Numer:</span>
                                            <hr />
                                            <b>{order.orderNumber}</b>
                                        </div>
                                        <div
                                            className='d-flex align-items-center'
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        >
                                            <span style={{ marginRight: '6px' }}>Status:</span>
                                            {ORDER_STATUS_ICONS_MOBILE[order.authorizationStatus === 'Autoryzowane' ? order.status : order.authorizationStatus]}
                                        </div>
                                    </div>
                                    <div>
                                        <div className='Order-block-item-content-left'>
                                            <span>Numer klienta:</span>
                                            <hr />
                                            <b>{order.customerOrderNumber}</b>
                                        </div>
                                        <div className='Order-block-item-content-right'>
                                            <span>Data:</span>
                                            <hr />
                                            <b>{order.date}</b>
                                        </div>
                                    </div>
                                    <Collapse in={props.openedMobile[index]}>
                                        <section>
                                            <div>
                                                <div className='Order-block-item-content-left'>
                                                    <span>Netto:</span>
                                                    <hr />
                                                    <b>{order.net.toFixed(2)}</b>
                                                </div>
                                                <div className='Order-block-item-content-right'>
                                                    <span>Brutto:</span>
                                                    <hr />
                                                    <b>{order.gross.toFixed(2)}</b>
                                                </div>
                                            </div>
                                            <div>
                                                <div className='Order-block-item-content-right'>
                                                    <span>Waga:</span>
                                                    <hr />
                                                    <b>{order.weight}</b>
                                                </div>
                                            </div>
                                            <div className='Order-block-item-content-left'>
                                                <span>Uwagi do zamówienia:</span>
                                                <hr />
                                                <b style={{
                                                    wordWrap: 'break-word',
                                                    maxWidth: '100%',
                                                    textAlign: 'right'
                                                }}
                                                >
                                                    {order.description}
                                                </b>
                                            </div>
                                            <div className='Order-block-item-content-left'>
                                                <span style={{ whiteSpace: 'nowrap' }}>Opis klienta:</span>
                                                <hr />
                                                <b style={{
                                                    wordWrap: 'break-word',
                                                    maxWidth: '100%',
                                                    textAlign: 'right'
                                                }}
                                                >
                                                    {order.customerDescription}
                                                </b>
                                            </div>
                                            <div style={{ marginBottom: '15px' }}>
                                                {permissionService.haveAccessToOperation('authorizeOrder') ?
                                                    <Button
                                                        variant='link'
                                                        style={{ color: 'black' }}
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                                            e.stopPropagation();
                                                            setOrderToAuthorize(order);
                                                            setShowAuthorizationConfirmation(true);
                                                        }}
                                                    >
                                                        <FaPenAlt size={24} />
                                                    </Button> : <></>}
                                                {order.isEditable && permissionService.haveAccessToOperation('manageOrder') ?
                                                    <Button
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                                            e.stopPropagation();
                                                            handleEditOrder(order);
                                                        }}
                                                        variant='link'
                                                        style={{ color: 'black' }}
                                                    >
                                                        <FaRegEdit size={24} />
                                                    </Button> :
                                                    <></>}
                                                {order.isPrintable ?
                                                    <Button
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                                            e.stopPropagation();
                                                            handleDownloadOrderPdf(order);
                                                        }}
                                                        variant='link'
                                                        style={{ color: 'black' }}
                                                    >
                                                        <FaRegFilePdf size={24} />
                                                    </Button> :
                                                    <></>}
                                                {order.isDeletable && permissionService.haveAccessToOperation('manageOrder') ?
                                                    <Button
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                                            e.stopPropagation();
                                                            setOrderToDelete(order);
                                                            setShowDeleteConfirmation(true);
                                                        }}
                                                        variant='link'
                                                        style={{ color: 'black' }}
                                                    >
                                                        <FaRegTrashAlt size={24} />
                                                    </Button> :
                                                    <></>}
                                                {order.isEditable && permissionService.haveAccessToOperation('manageOrder') ?
                                                    <Button
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                                            e.stopPropagation();
                                                            setOrderToCopy(order);
                                                            setShowCopyOrderConfirmation(true);
                                                        }}
                                                        variant='link'
                                                        style={{ color: 'black' }}
                                                    >
                                                        <FaCopy size={24} />
                                                    </Button> :
                                                    <></>}
                                                {order.isEditable && permissionService.haveAccessToOperation('manageOrder') ?
                                                    <Button
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                                            e.stopPropagation();
                                                            setOrderToAppend(order);
                                                            setShowAppendOrderConfirmation(true);
                                                        }}
                                                        variant='link'
                                                        style={{ color: 'black' }}
                                                    >
                                                        <FaPlus size={24} />
                                                    </Button> : <></>}
                                            </div>
                                        </section>
                                    </Collapse>
                                </div>
                            ))}
                            <div>
                                {(pageNumber + 1) * props.ordersState.pageSize > _.size(selectedFilteredOrders) ? (<div></div>) : (
                                    <Button
                                        onClick={() => {
                                            setPageNumber(pageNumber + 1);
                                        }}
                                        style={{ marginTop: 10 }}
                                    >
                                        Pokaż więcej
                                    </Button>
                                )}
                            </div>
                        </>}
                </div>
            )}
            {isDesktop && !isFetchingOrders && (
                <Row className='justify-content-end'>
                    <Col xs='auto' style={{ marginLeft: '15px' }}>
                        <div className='d-flex justify-content-end'>
                            <Pagination
                                selected={pageNumber}
                                size={numberOfPages}
                                onSelect={(p) => setPageNumber(p)}
                            />
                        </div>
                    </Col>
                </Row>
            )}
            <Modal show={showDeleteConfirmation} onHide={() => setShowDeleteConfirmation(false)} backdrop='static'>
                <Modal.Header className='TableView-modal-header'>
                    <Modal.Title>Usuń zamówienie</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Czy na pewno chcesz usunąć zamówienie o numerze ${orderToDelete?.orderNumber} ?`}</Modal.Body>
                <Modal.Footer className='TableView-modal-footer'>
                    <ButtonComponent variant='danger' text='Usuń' margin='0' width='100px' onClick={() => handleOrderDelete()} brandColoredButton={false} />
                    <ButtonComponent variant='dark' text='Anuluj' margin='0' width='100px' onClick={() => setShowDeleteConfirmation(false)} brandColoredButton={false} />
                </Modal.Footer>
            </Modal>
            <Modal show={showDeleteStatus} onHide={() => setShowDeleteStatus(false)} backdrop='static'>
                <Modal.Header className='TableView-modal-header'>
                    <Modal.Title>Zamówienie usunięte</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Poprawnie usunięto zamówienie o numerze ${orderToDelete?.orderNumber}`}</Modal.Body>
                <Modal.Footer className='TableView-modal-footer one-button'>
                    <ButtonComponent onHoverAnimation={3} text='Ok' margin='0' onClick={() => setShowDeleteStatus(false)} />
                </Modal.Footer>
            </Modal>
            <Modal show={showCopyOrderConfirmation} onHide={() => setShowCopyOrderConfirmation(false)} backdrop='static'>
                <Modal.Header className='TableView-modal-header'>
                    <Modal.Title>Kopiuj zamówienie</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Ta akcja spowoduje wyczyszcenie koszyka i zastąpienie jego zawartości produktami zamówienia ${orderToCopy?.orderNumber}`}</Modal.Body>
                <Modal.Footer className='TableView-modal-footer'>
                    <ButtonComponent text='Kontynuuj' margin='0' width='100px' onClick={() => handleCopyOrder(orderToCopy!)} />
                    <ButtonComponent variant='dark' text='Anuluj' margin='0' width='100px' brandColoredButton={false} onClick={() => setShowCopyOrderConfirmation(false)} />
                </Modal.Footer>
            </Modal>
            <Modal show={showAppendOrderConfirmation} onHide={() => setShowAppendOrderConfirmation(false)} backdrop='static'>
                <Modal.Header className='TableView-modal-header'>
                    <Modal.Title>Wystaw domówienie</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Akcja domówienia spowoduje wyczyszcenie koszyka i zastąpienie jego zawartości produktami zamówienia ${orderToAppend?.orderNumber}`}</Modal.Body>
                <Modal.Footer className='TableView-modal-footer'>
                    <ButtonComponent text='Kontynuuj' margin='0' width='100px' onClick={() => handleAppendOrder(orderToAppend!)} />
                    <ButtonComponent variant='dark' text='Anuluj' brandColoredButton={false} margin='0' width='100px' onClick={() => setShowAppendOrderConfirmation(false)} />
                </Modal.Footer>
            </Modal>
            <Modal show={showAuthorizationConfirmation} onHide={() => setShowAuthorizationConfirmation(false)} backdrop='static'>
                <Modal.Header className='TableView-modal-header'>
                    <Modal.Title>{orderToAuthorize?.authorizationStatus === 'Autoryzowane' ? 'Cofnij autoryzację' : 'Autoryzuj'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Czy na pewno chcesz ${orderToAuthorize?.authorizationStatus === 'Autoryzowane' ? 'cofnąć autoryzację zamówienia' : 'autoryzować zamówienie'} o numerze ${orderToAuthorize?.orderNumber} ?`}</Modal.Body>
                <Modal.Footer className='TableView-modal-footer'>
                    <ButtonComponent variant={orderToAuthorize?.authorizationStatus === 'Autoryzowane' ? 'danger' : 'success'} text={orderToAuthorize?.authorizationStatus === 'Autoryzowane' ? 'Cofnij autoryzację' : 'Autoryzuj'} margin='0' onClick={() => handleUpdateOrderAuthorization()} brandColoredButton={false} />
                    <ButtonComponent variant='dark' text='Anuluj' margin='0' onClick={() => setShowAuthorizationConfirmation(false)} brandColoredButton={false} />
                </Modal.Footer>
            </Modal>
            <Modal show={showAuthorizationStatus} onHide={() => setShowAuthorizationStatus(false)} backdrop='static'>
                <Modal.Header className='TableView-modal-header'>
                    <Modal.Title>Status autoryzacji</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Poprawnie zmieniono autoryzację zamówienia o numerze ${orderToAuthorize?.orderNumber}`}</Modal.Body>
                <Modal.Footer className='TableView-modal-footer one-button'>
                    <ButtonComponent onHoverAnimation={3} text='Dalej' margin='0' onClick={() => setShowAuthorizationStatus(false)} />
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default TableView;
